// SeatingAreaPicker — guest picks Inside / Outside / Patio / etc. when
// booking a service that has seating_areas configured. Extracted from
// DineEnhancements so non-dining service types (wine tastings, picnics)
// can render it standalone.
//
// Two presentations share the same data + lightbox:
//   • Inline (datetime layout): SeatingAreaField is a <select> that drops
//     into the booking-setup grid right after WHEN, and SeatingAreaPreview
//     renders the thumbnail strip below the grid (selected area highlighted).
//     ServicePanel composes these into BookingSetup.
//   • Standalone (other layouts): SeatingAreaPicker renders a chip row +
//     the same SeatingAreaPreview block, after BookingSetup.
//
// Previews (hero + strip) only render when the service's
// show_seating_previews flag is true AND at least one area has an
// image_url; otherwise only the selector (field or chips) shows, with the
// selected area's description note underneath when present.

import { useEffect, useMemo, useState } from "react";
import { optimizedImageUrl } from "../lib/image";
import { supabase } from "../config";
import { WidgetLayer } from "./WidgetLayer";

export interface SeatingArea {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  badge: string | null;
  image_url: string | null;
  show_seating_previews: boolean;
  display_order: number;
}

// (id, name) — name is null when the selection clears. Lets the parent
// ServicePanel put a human-readable label into cart metadata without
// re-fetching.
type AreaChange = (id: string | null, name: string | null) => void;

interface Props {
  serviceId: string;
  selectedAreaId: string | null;
  onAreaChange: AreaChange;
}

// Fetches the seating areas applicable to a service. Pass `null` to skip
// the fetch entirely (e.g. when the service type never shows seating) —
// the hook then stays at `null` without hitting the RPC.
export function useSeatingAreas(serviceId: string | null): SeatingArea[] | null {
  const [areas, setAreas] = useState<SeatingArea[] | null>(null);

  useEffect(() => {
    if (!serviceId) {
      setAreas(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase.rpc("service_seating_areas", {
        p_service_id: serviceId,
      });
      if (cancelled) return;
      setAreas((data ?? []) as SeatingArea[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [serviceId]);

  return areas;
}

// True when the tenant has both enabled previews and uploaded at least one
// image — the gate for rendering the hero + thumbnail strip.
function useShowPreviews(areas: SeatingArea[]): boolean {
  return useMemo(() => {
    if (areas.length === 0) return false;
    // All rows carry the same flag from the joined service; pick any.
    const first = areas[0];
    if (!first || !first.show_seating_previews) return false;
    return areas.some((a) => a.image_url);
  }, [areas]);
}

// The seating selector as a labelled <select>, styled to match the WHEN /
// DURATION fields so it sits inline inside .vt-setup-grid.
export function SeatingAreaField({
  areas,
  selectedAreaId,
  onAreaChange,
  label = "Where",
}: {
  areas: SeatingArea[];
  selectedAreaId: string | null;
  onAreaChange: AreaChange;
  label?: string;
}) {
  if (areas.length === 0) return null;
  return (
    <div className="vt-setup-field">
      <label>{label}</label>
      <select
        value={selectedAreaId ?? ""}
        onChange={(e) => {
          const id = e.target.value || null;
          const name = id ? (areas.find((a) => a.id === id)?.name ?? null) : null;
          onAreaChange(id, name);
        }}
      >
        <option value="">No preference</option>
        {areas.map((a) => (
          <option key={a.id} value={a.id}>
            {a.name}
            {a.badge ? ` · ${a.badge}` : ""}
          </option>
        ))}
      </select>
    </div>
  );
}

// Visual block shown below the selector: a thumbnail strip of every area,
// with the selected area's thumbnail highlighted. Clicking a thumbnail
// opens a fullscreen lightbox. Renders only the description note (or
// nothing) when previews aren't configured.
export function SeatingAreaPreview({
  areas,
  selectedAreaId,
}: {
  areas: SeatingArea[];
  selectedAreaId: string | null;
}) {
  const [lightboxArea, setLightboxArea] = useState<SeatingArea | null>(null);
  const showPreviews = useShowPreviews(areas);
  const selectedArea = areas.find((a) => a.id === selectedAreaId) ?? null;

  if (!showPreviews) {
    return selectedArea?.description ? (
      <p className="vt-dine-area-note">{selectedArea.description}</p>
    ) : null;
  }

  return (
    <div className="vt-seating-preview-wrap">
      <div
        className={`vt-seating-previews${selectedAreaId ? " vt-seating-previews--has-selection" : ""}`}
        role="group"
        aria-label="Seating area previews"
      >
        {areas.map((a) =>
          a.image_url ? (
            <button
              key={a.id}
              type="button"
              className={`vt-seating-preview${selectedAreaId === a.id ? " vt-seating-preview-active" : ""}`}
              onClick={() => setLightboxArea(a)}
              aria-label={`Preview ${a.name}`}
            >
              {selectedAreaId === a.id && (
                <span className="vt-seating-preview-check" aria-hidden="true">
                  ✓
                </span>
              )}
              <img
                src={optimizedImageUrl(a.image_url, { width: 600 })}
                alt={a.name}
                loading="lazy"
                decoding="async"
              />
              <span className="vt-seating-preview-label">{a.name}</span>
            </button>
          ) : null,
        )}
      </div>

      {selectedArea?.description && (
        <p className="vt-dine-area-note">{selectedArea.description}</p>
      )}

      {lightboxArea && (
        <SeatingAreaLightbox
          area={lightboxArea}
          onClose={() => setLightboxArea(null)}
        />
      )}
    </div>
  );
}

// Standalone presentation (chip row + preview) used by layouts that have
// no WHEN/DURATION grid to embed the field into.
export function SeatingAreaPicker({
  serviceId,
  selectedAreaId,
  onAreaChange,
}: Props) {
  const areas = useSeatingAreas(serviceId);

  if (!areas || areas.length === 0) return null;

  return (
    <div className="vt-seating-picker">
      <div className="vt-dine-section-label">Where would you like to sit?</div>
      <div className="vt-dine-chip-row">
        {areas.map((a) => (
          <button
            key={a.id}
            type="button"
            className={`vt-chip ${selectedAreaId === a.id ? "vt-chip-active" : ""}`}
            onClick={() =>
              selectedAreaId === a.id
                ? onAreaChange(null, null)
                : onAreaChange(a.id, a.name)
            }
          >
            {a.name}
            {a.badge && <span className="vt-chip-badge">{a.badge}</span>}
          </button>
        ))}
        <button
          type="button"
          className={`vt-chip ${selectedAreaId === null ? "vt-chip-active" : ""}`}
          onClick={() => onAreaChange(null, null)}
        >
          No preference
        </button>
      </div>

      <SeatingAreaPreview areas={areas} selectedAreaId={selectedAreaId} />
    </div>
  );
}

function SeatingAreaLightbox({
  area,
  onClose,
}: {
  area: SeatingArea;
  onClose: () => void;
}) {
  // Close on Escape — keeps keyboard navigation working when the
  // customer hits the obvious shortcut.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!area.image_url) return null;

  return (
    <WidgetLayer>
      <div
        className="vt-lightbox"
        role="dialog"
        aria-modal="true"
        aria-label={`${area.name} — preview`}
        onClick={onClose}
      >
        <button
          type="button"
          className="vt-lightbox-close"
          onClick={onClose}
          aria-label="Close preview"
        >
          ×
        </button>
        <figure
          className="vt-lightbox-figure"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={optimizedImageUrl(area.image_url, { width: 1000 })}
            alt={area.name}
            decoding="async"
          />
          <figcaption className="vt-lightbox-caption">
            <strong>{area.name}</strong>
            {area.badge && <span className="vt-chip-badge">{area.badge}</span>}
            {area.description && <p>{area.description}</p>}
          </figcaption>
        </figure>
      </div>
    </WidgetLayer>
  );
}
